/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Wire contracts for the MyHub (DVR) service — cloudcore-myshows v1.
 *
 * Domain layer, not ViewModel: these mirror the cloudcore-myshows 0.1.0 schemas
 * for `/v1/myHub/carousels`, `/v1/myHub/myHubFolder/...` and related endpoints,
 * with field names and enum values kept identical to the generated models under
 * `client-code/myshows`. They describe what the service says, which is a
 * business fact independent of any screen — so they live here beside
 * `TContentDetailDto`, and both the ViewModel mappers and the View can depend on
 * them without either depending on the other.
 *
 * Consuming them from a View is limited to the affordance enums
 * ({@link TMyHubActionType}, {@link TMyHubBulkActionType}), which name the
 * actions a row or folder offers. The response and item shapes below are for the
 * mappers in `viewModel/myHub` — a View must render the presentation-ready types
 * those mappers produce, never a raw response.
 */

// ── Enumerations ──────────────────────────────────────────────────────────────

/** Discriminator for the MyHubItem polymorphic type. */
export enum TMyHubType {
  CONTENT = "ContentMyHubItem",
  RECORDING = "RecordingMyHubItem",
  FOLDER = "FolderMyHubItem"
}

/** Tile-shape / layout hint for a carousel row. */
export enum TMyHubCarouselLayout {
  LANDSCAPE_HORIZONTAL = "landscape_horizontal",
  LANDSCAPE_GRID = "landscape_grid",
  PIP_LANDSCAPE_HORIZONTAL = "pip_landscape_horizontal",
  PIP_LANDSCAPE_GRID = "pip_landscape_grid",
  POSTER_HORIZONTAL = "poster_horizontal",
  LOGO_HORIZONTAL = "logo_horizontal"
}

/** Content program type — maps to display/render decisions in the view. */
export enum TMyHubProgramType {
  EVENT = "event",
  GAME = "game",
  MATCH = "match",
  RACE = "race",
  MOVIE = "movie",
  EPISODE = "episode",
  SHOW = "show"
}

/**
 * Overlay badge displayed on a tile.
 * Note: partial-watch progress is carried by percentageWatched, NOT a badge.
 */
export enum TMyHubBadge {
  NEW = "new",
  RECORDING = "recording",
  WATCHED = "watched",
  SAVED = "saved"
}

/** Single-item action — each maps to a cloudcore-recordings API call. */
export enum TMyHubActionType {
  PLAY = "play",
  DETAILS = "details",
  DELETE = "delete",
  SAVE = "save",
  UNSAVE = "unsave",
  STOP = "stop",
  EXTEND = "extend",
  UNDELETE = "undelete",
  PERMANENTLY_DELETE = "permanentlyDelete"
}

/** Collection/tab-scoped bulk action (folder header + flat-list bulk button). */
export enum TMyHubBulkActionType {
  RECORDING_OPTIONS = "recordingOptions",
  START_SERIES_RECORDING = "startSeriesRecording",
  CANCEL_SERIES_RECORDINGS = "cancelSeriesRecordings",
  DELETE_ALL_RECORDINGS = "deleteAllRecordings",
  PERMANENTLY_DELETE_ALL = "permanentlyDeleteAll"
}

/** Determines how the filter/tab's content area is laid out. */
export enum TMyHubFilterLayout {
  CAROUSEL = "carousel",
  LIST = "list",
  GRID = "grid"
}

/** Folder type for a FolderMyHubItem. */
export enum TMyHubFolderType {
  UNIFIED_COLLECTION = "unifiedCollection",
  MOVIE = "movie",
  SPECIAL = "special"
}

// ── Item types ────────────────────────────────────────────────────────────────

/**
 * Base grid tile — fields common to every MyHub item.
 * Concrete shape is chosen by the myHubItemType discriminator.
 */
export interface TMyHubItem {
  myHubItemId: string;
  myHubItemType: TMyHubType;
  title: string;
  /** Id used to fetch tile art (poster/landscape) from the image service. */
  digItemId?: string;
  isAdult?: boolean;
  badges?: TMyHubBadge[];
  availableActions?: TMyHubActionType[];
}

/**
 * One entry of an item's `internalRatingList` — a rating system and a value
 * in it. A key into the internal ratings table, resolved to a display name
 * ("PG-13") by `resolveInternalRatingNames`; never shown as-is.
 */
export interface TMyHubInternalRating {
  typeId?: string | number;
  valueId?: string | number;
}

/**
 * A single playable asset (movie / episode) that is NOT a recording.
 * Extends TMyHubItem with content-level metadata.
 */
export interface TContentMyHubItem extends TMyHubItem {
  myHubItemType: TMyHubType.CONTENT;
  /** Content id — used for detail-screen navigation. */
  contentId?: string;
  programType?: TMyHubProgramType;
  /** Duration in seconds; client formats "2h 6m" — see formatSavedDuration. */
  duration?: number;
  /** Display rating ("PG-13") — the fallback when `internalRatingList` does
   * not resolve. */
  rating?: string;
  /** Internal rating ids, resolved through the internal ratings table the
   * same way previews resolve theirs. Preferred over `rating`. */
  internalRatingList?: TMyHubInternalRating[];
  /** Watch progress 0–100; drives the progress bar on the tile. */
  percentageWatched?: number;
  movieYear?: string;
  seasonOrYear?: number;
  episodeNumber?: number;
  episodeTitle?: string;
}

/**
 * A recording or scheduled recording of a single asset.
 * - Recorded / Saved / Deleted items carry recordingId.
 * - Scheduled items are offer-keyed (offerId); recordingId is absent.
 * - Series-level operations use the parent folder's unifiedCollectionId.
 */
export interface TRecordingMyHubItem extends Omit<
  TContentMyHubItem,
  "myHubItemType"
> {
  myHubItemType: TMyHubType.RECORDING;
  /** Present for actual recordings (recorded/saved/deleted); absent for Scheduled. */
  recordingId?: string;
  /** Key for scheduled + in-progress recordings (create/modify/cancel/stop). */
  offerId?: string;
  stationId?: string;
  startTime?: string;
  endTime?: string;
  /** Expiry date-time for recorded content. */
  expiresOn?: string;
  /** For the Saved tab — "Saved on 5/18/2026". */
  savedOn?: string;
  /** For the Deleted tab — "Deleted on 5/18/2026". */
  deletedOn?: string;
}

/**
 * A series / collection folder grouping multiple recordings or scheduled items.
 */
export interface TFolderMyHubItem extends TMyHubItem {
  myHubItemType: TMyHubType.FOLDER;
  unifiedCollectionId: string;
  folderType?: TMyHubFolderType;
  recordingCount?: number;
}

/** Discriminated union of all concrete MyHub item types. */
export type TMyHubItemUnion =
  | TContentMyHubItem
  | TRecordingMyHubItem
  | TFolderMyHubItem;

// ── Carousel / screen-load types ──────────────────────────────────────────────

/** A single carousel row as returned by the API. */
export interface TMyHubCarousel {
  /** Opaque server-driven id — used for paging (GET /v1/myHub/carousels/{carouselId}). */
  carouselId: string;
  /**
   * Summary line and bulk button above this row — present only on the single
   * carousel the flat Saved / Deleted tabs return.
   */
  header?: TMyHubCarouselHeader;
  /** Localized display label ("All Recorded", "TV Series", ...). */
  title?: string;
  layout: TMyHubCarouselLayout;
  /** True carousel length — drives "View All (N)" and offset/count paging. */
  resolvedCount?: number;
  items: TMyHubItemUnion[];
}

/**
 * Carousel-level summary for the flat Saved/Deleted tabs — the summary line and
 * the bulk button above the list.
 *
 * Present only on those tabs' single carousel (absent for Recorded/Scheduled).
 * It rode on the carousels response itself until cloudcore-myshows build 421
 * moved it onto the carousel and renamed it from `MyHubListHeader`.
 */
export interface TMyHubCarouselHeader {
  totalCount?: number;
  usedMinutes?: number;
  limitMinutes?: number;
  bulkAction?: TMyHubBulkActionType;
}

/** The row and item the user last had selected, for restoring focus. */
export interface TSelectedMyHubItem {
  index: number;
  myHubItemId: string;
}

/**
 * Screen-load response for one MyHub filter/tab
 * (GET /v1/myHub/carousels?myHubFilter=<value>).
 *
 * Always a list of carousels, so one renderer covers every tab:
 *   - Recorded / Scheduled: several carousels (horizontal rows).
 *   - Saved / Deleted: exactly ONE carousel — the flat vertical list — carrying
 *     `header`, the tab summary and bulk action.
 *
 * Mirrors the generated `MyHubCarouselList` (cloudcore-myshows 0.1.0).
 */
export interface TMyHubCarouselList {
  carousels: TMyHubCarousel[];
  /** Localized sort-order label; editorial, not derivable from the data. */
  sortOrder?: string;
  selectedMyHubItem?: TSelectedMyHubItem;
}

/**
 * One collection/folder with its items and folder-level actions — the response
 * behind the manage-series screen (Figma node 71:4516).
 *
 * `GET /v1/myHub/myHubFolder/unifiedCollection/{unifiedCollectionId}`, whose
 * required `myHubFilter` chooses the recorded or the scheduled variant: that
 * decides `folderTitle`, which `folderActions` appear, and each item's
 * `availableActions`.
 *
 * Mirrors the generated `MyHubFolderDetail` (cloudcore-myshows 0.1.0). Views
 * must NOT consume this directly — use `myHubSeriesMappers.ts`.
 */
export interface TMyHubFolderDetail {
  unifiedCollectionId?: string;
  /**
   * Server-owned title — "Yellowstone Recordings" for the recorded variant,
   * "Yellowstone Scheduled Recordings" for the scheduled one.
   */
  folderTitle: string;
  itemCount?: number;
  /** Total runtime of the folder's recordings; the client renders "12 hours". */
  totalDurationMinutes?: number;
  /** Collection-level header buttons; differs by recorded / scheduled. */
  folderActions?: TMyHubBulkActionType[];
  /** The folder's items, in the same discriminated shape the grid uses. */
  items: TMyHubItemUnion[];
}
